/**
 * ============================================================
 * MENAHUB LANGUAGE SWITCHER WITH RTL SUPPORT
 * Handles Arabic/English switching with:
 * - RTL/LTR direction switching
 * - Image swapping for Arabic/English
 * - Content translation
 * - Carousel direction
 * ============================================================
 */

(function() {
  'use strict';

  // Configuration
  const STORAGE_KEY = 'menahub_language';
  const STORAGE_DIR_KEY = 'menahub_direction';
  
  // Language switcher class
  class LanguageSwitcher {
    constructor() {
      this.currentLang = this.getStoredLanguage();
      this.currentDir = this.currentLang === 'ar' ? 'rtl' : 'ltr';
      this.init();
    }

    init() {
      // Set initial direction on page load
      this.applyDirection(this.currentDir);
      
      // Swap images based on language
      this.swapImages();
      
      // Initialize language switcher buttons
      this.initSwitchers();
      
      // Fix carousels for RTL
      this.initCarousels();
      
      // Update active language indicator
      this.updateActiveLanguage();
    }

    getStoredLanguage() {
      // Check localStorage first
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return stored;
      
      // Check HTML lang attribute
      const htmlLang = document.documentElement.getAttribute('lang');
      if (htmlLang && htmlLang.startsWith('ar')) return 'ar';
      
      // Default to English
      return 'en';
    }

    applyDirection(direction) {
      document.documentElement.setAttribute('dir', direction);
      document.documentElement.setAttribute('lang', direction === 'rtl' ? 'ar' : 'en');
      document.body.setAttribute('dir', direction);
      
      // Store in localStorage
      localStorage.setItem(STORAGE_DIR_KEY, direction);
      
      // Update body class
      if (direction === 'rtl') {
        document.body.classList.add('rtl');
        document.body.classList.remove('ltr');
      } else {
        document.body.classList.add('ltr');
        document.body.classList.remove('rtl');
      }
    }

    swapImages() {
      // Swap images with data-img-ar and data-img-en attributes
      const images = document.querySelectorAll('[data-img-ar], [data-img-en]');
      
      images.forEach(img => {
        const arImg = img.getAttribute('data-img-ar');
        const enImg = img.getAttribute('data-img-en');
        
        if (this.currentLang === 'ar' && arImg) {
          img.src = arImg;
          if (img.srcset) img.srcset = arImg;
        } else if (this.currentLang === 'en' && enImg) {
          img.src = enImg;
          if (img.srcset) img.srcset = enImg;
        }
      });

      // Swap background images
      const bgElements = document.querySelectorAll('[data-bg-ar], [data-bg-en]');
      
      bgElements.forEach(el => {
        const arBg = el.getAttribute('data-bg-ar');
        const enBg = el.getAttribute('data-bg-en');
        
        if (this.currentLang === 'ar' && arBg) {
          el.style.backgroundImage = `url(${arBg})`;
        } else if (this.currentLang === 'en' && enBg) {
          el.style.backgroundImage = `url(${enBg})`;
        }
      });
    }

    initSwitchers() {
      // Find all language switcher elements
      const switchers = document.querySelectorAll('[data-language-switcher]');
      
      switchers.forEach(switcher => {
        switcher.addEventListener('click', (e) => {
          e.preventDefault();
          const targetLang = switcher.getAttribute('data-language') || switcher.getAttribute('data-lang');
          
          if (targetLang && targetLang !== this.currentLang) {
            this.switchLanguage(targetLang);
          }
        });
      });

      // Handle select dropdown
      const langSelect = document.querySelector('#language-selector, [name="language"]');
      if (langSelect) {
        langSelect.value = this.currentLang;
        langSelect.addEventListener('change', (e) => {
          this.switchLanguage(e.target.value);
        });
      }
    }

    switchLanguage(newLang) {
      // Store new language
      this.currentLang = newLang;
      localStorage.setItem(STORAGE_KEY, newLang);
      
      // Update direction
      const newDir = newLang === 'ar' ? 'rtl' : 'ltr';
      this.currentDir = newDir;
      this.applyDirection(newDir);
      
      // Swap images
      this.swapImages();
      
      // Reload carousels for new direction
      this.reloadCarousels();
      
      // Update active language
      this.updateActiveLanguage();
      
      // Trigger custom event
      document.dispatchEvent(new CustomEvent('languageChanged', {
        detail: { language: newLang, direction: newDir }
      }));
      
      // Show notification
      this.showNotification(newLang === 'ar' ? 'تم التبديل إلى العربية' : 'Switched to English');
      
      // Reload page to apply Shopify translations
      setTimeout(() => {
        window.location.href = this.updateUrlLanguage(window.location.href, newLang);
      }, 300);
    }

    updateUrlLanguage(url, lang) {
      const urlObj = new URL(url);
      
      // Update or add language parameter
      urlObj.searchParams.set('locale', lang === 'ar' ? 'ar' : 'en');
      
      return urlObj.toString();
    }

    updateActiveLanguage() {
      // Update active state on language buttons
      const allSwitchers = document.querySelectorAll('[data-language-switcher]');
      
      allSwitchers.forEach(switcher => {
        const lang = switcher.getAttribute('data-language') || switcher.getAttribute('data-lang');
        
        if (lang === this.currentLang) {
          switcher.classList.add('active');
          switcher.setAttribute('aria-current', 'true');
        } else {
          switcher.classList.remove('active');
          switcher.removeAttribute('aria-current');
        }
      });
    }

    initCarousels() {
      // Initialize Slick Carousel with RTL support
      if (typeof jQuery !== 'undefined' && jQuery.fn.slick) {
        jQuery('.slick-slider').each(function() {
          if (!jQuery(this).hasClass('slick-initialized')) {
            jQuery(this).slick({
              rtl: document.documentElement.getAttribute('dir') === 'rtl',
              autoplay: true,
              autoplaySpeed: 3000,
              arrows: true,
              dots: true
            });
          }
        });
      }

      // Initialize Swiper with RTL support
      if (typeof Swiper !== 'undefined') {
        const swiperElements = document.querySelectorAll('.swiper');
        
        swiperElements.forEach(el => {
          if (!el.swiper) {
            new Swiper(el, {
              direction: 'horizontal',
              rtl: document.documentElement.getAttribute('dir') === 'rtl',
              loop: true,
              autoplay: {
                delay: 3000,
                disableOnInteraction: false,
              },
              navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
              },
              pagination: {
                el: '.swiper-pagination',
                clickable: true,
              },
            });
          }
        });
      }
    }

    reloadCarousels() {
      // Reload Slick Carousel
      if (typeof jQuery !== 'undefined' && jQuery.fn.slick) {
        jQuery('.slick-slider').slick('unslick');
        this.initCarousels();
      }

      // Reload Swiper
      if (typeof Swiper !== 'undefined') {
        const swipers = document.querySelectorAll('.swiper');
        swipers.forEach(el => {
          if (el.swiper) {
            el.swiper.destroy();
          }
        });
        this.initCarousels();
      }
    }

    showNotification(message) {
      // Create notification element
      const notification = document.createElement('div');
      notification.className = 'language-switch-notification';
      notification.textContent = message;
      notification.style.cssText = `
        position: fixed;
        top: 80px;
        ${this.currentDir === 'rtl' ? 'left' : 'right'}: 20px;
        background: #25D366;
        color: white;
        padding: 12px 24px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 999999;
        font-size: 14px;
        font-weight: 600;
        animation: slideIn 0.3s ease;
      `;
      
      document.body.appendChild(notification);
      
      // Remove after 2 seconds
      setTimeout(() => {
        notification.style.animation = 'slideOut 0.3s ease';
        setTimeout(() => notification.remove(), 300);
      }, 2000);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.menahubLanguageSwitcher = new LanguageSwitcher();
    });
  } else {
    window.menahubLanguageSwitcher = new LanguageSwitcher();
  }

  // Add animation styles
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideIn {
      from {
        transform: translateX(${document.documentElement.dir === 'rtl' ? '-' : ''}100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }
    
    @keyframes slideOut {
      from {
        transform: translateX(0);
        opacity: 1;
      }
      to {
        transform: translateX(${document.documentElement.dir === 'rtl' ? '-' : ''}100%);
        opacity: 0;
      }
    }
    
    [data-language-switcher] {
      cursor: pointer;
      transition: all 0.2s ease;
    }
    
    [data-language-switcher]:hover {
      opacity: 0.8;
    }
    
    [data-language-switcher].active {
      font-weight: bold;
      color: #ff6b35;
    }
  `;
  document.head.appendChild(style);

})();